/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import powerDialerStore from "Stores/powerDialerStore";
import DialerServiceData from "Stores/DialerServiceStore";
import userDetail from "Stores/userDetail";
import PhoneNumberDropdown from "Components/DailerService/PhoneNumberDropdown";
import { toasterState } from "Components/common/toaster";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import { STATES, keyAction, noPhonePrompt, contactClock } from "Utils/powerDialerMachine";
import { askLeave } from "./leaveGuard";
import { RunningView, RunningFoot, DoneView, DoneFoot } from "./PowerDialerSession";
import { globalCallState } from "Stores/GlobalCallState";
import "./powerDialer.scss";

const BODY_CLASS = "sgPowerDialerDocked";
const LIVE_CLASS = "sgPowerDialerLive";
const CALLS_LOCKED = "Power dialer session is ongoing. End the session to start calling manually.";
const DIALER_SETTINGS = "/settings/dialer/general";

const promptNoCallingNumber = history => {
  confirmationPopupState.setPopupValues({
    content: (
      <div className="pdPopup pdNoNumber">
        <span className="pdPopupIcon">
          <i className="material-symbols-outlined">phone_disabled</i>
        </span>
        <div className="pdPopupTitle">Add a calling number to start dialing</div>
        <div className="pdPopupText">
          The power dialer calls from one of your numbers. Buy a number or pick one you already own in Dialer
          settings, then come back and press Start dialing.
        </div>
      </div>
    ),
    dynamicDom: true,
    actionBtnText: "Go to dialer settings",
    cancelBtnText: "Not now",
    // The popup closes itself after this returns; navigate after that so a leave prompt isn't hidden.
    callback: () => setTimeout(() => history.push(DIALER_SETTINGS), 0)
  });
  confirmationPopupState.setShowPopup(true);
};
const NO_NUMBER = ["no-number", "finding", "not-found"];
const inPowerMode = loc => loc.pathname.startsWith("/tasks/start/") && /mode=power/.test(loc.search || "");

const Check = ({ ok = true, children, action = null }) => (
  <div className="pdCheck">
    <span className={`pdDot ${ok ? "ok" : "warn"}`} />
    <span className="pdCheckText">{children}</span>
    {action}
  </div>
);

const CallingFrom = observer(() => {
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const history = useHistory();
  const { dialerDefaultNo, dialerPhoneList } = DialerServiceData;
  const number = dialerDefaultNo !== "purchaseNumber" ? dialerDefaultNo : "";
  const missing = !number && checked;

  useEffect(() => {
    if (number) return;
    DialerServiceData.getDialerPhoneList(false).finally(() => setChecked(true));
  }, []);

  const select = item => {
    toasterState.setToastMsg("Please wait...", "info");
    DialerServiceData.updateDefaultPhoneNumber({ ph_number: item.value });
  };

  return (
    <Check
      ok={!!number}
      action={
        missing ? (
          <button type="button" className="pdLink" onClick={() => history.push(DIALER_SETTINGS)}>
            Set up
          </button>
        ) : (
          dialerPhoneList?.length > 1 && (
          <span className="pdChangeWrap">
            <button type="button" className="pdLink" onClick={() => setOpen(true)}>
              Change
            </button>
            {open && (
              <PhoneNumberDropdown
                phoneNumbers={dialerPhoneList.map((value, i) => ({ value, key: `pdNo${i}` }))}
                selectedNumber={number}
                onSelect={select}
                onClose={() => setOpen(false)}
              />
            )}
          </span>
          )
        )
      }
    >
      {number
        ? `Calling from ${formatPhoneNumberIntl(number) || number}`
        : missing
          ? "No calling number set up"
          : "Loading calling number…"}
    </Check>
  );
});

const StartView = observer(() => {
  const history = useHistory();
  const { queue, skipped, settings, noPhoneMode, multiTaskContacts } = powerDialerStore;
  const flags = userDetail?.userFeatureFlag || {};
  const credits = +flags.dialerCredits || 0;
  const freeMinutes = (+flags.usFreeCallingMinutesEligible || 0) - (+flags.usFreeCallingMinutesUsed || 0);
  const noNumber = queue.filter(q => NO_NUMBER.includes(q.status) || q.reason === "no phone number").length;
  const otherSkipped = skipped.filter(s => s.reason !== "no phone number");
  const dialable = powerDialerStore.dialableCount;
  const finding = noPhoneMode === "find";

  return (
    <>
      <div className="pdBody pdStartBody">
        <div className="pdTitle">
          {dialable} call{dialable === 1 ? "" : "s"}
        </div>
        <div className="pdSub">Dials each one for you. You only step in when someone picks up.</div>
        {multiTaskContacts > 0 && (
          <div className="pdSub">
            {multiTaskContacts === 1
              ? "1 contact has more than one call task · called once"
              : `${multiTaskContacts} contacts have more than one call task · each called once`}
          </div>
        )}
        <div>
          <CallingFrom />
          <Check ok={credits > 0.25 || freeMinutes > 1}>
            ${credits.toFixed(2)} credits
            {freeMinutes > 0 ? ` · ${freeMinutes} free US minutes` : ""}
          </Check>
          <Check
            action={
              <button type="button" className="pdLink" onClick={() => history.push("/settings/dialer/voicemail")}>
                Settings
              </button>
            }
          >
            Recording {flags.recordingEnabled ? "on" : "off"} · voicemail drop{" "}
            {settings.autoVoicemailDrop ? "is automatic" : "asks first"}
          </Check>
          {noNumber > 0 && (
            <Check ok={false}>
              {noNumber} {noNumber === 1 ? "has" : "have"} no phone number
              {finding
                ? " · finding them while you call"
                : queue.some(q => q.status === "no-number")
                  ? ""
                  : " · skipped, their tasks stay due"}
            </Check>
          )}
          {otherSkipped.length > 0 && (
            <Check ok={false}>
              {otherSkipped.length} skipped · {[...new Set(otherSkipped.map(s => s.reason))].join(", ")}
            </Check>
          )}
        </div>
        <button
          type="button"
          className="pdPrimaryBtn"
          disabled={!dialable}
          onClick={() => powerDialerStore.dial()}
        >
          <i className="material-symbols-outlined">call</i>
          Start dialing <span className="pdKbd">Enter</span>
        </button>
        <div className="pdSecTitle">Queue</div>
        <div className="pdQueueList">
          {queue
            .filter(q => q.status !== "removed")
            .map(q => (
              <div className="pdQueueRow" key={q.taskId}>
                <span className="pdQueueName">{q.name || "Unnamed contact"}</span>
                {q.status === "skipped" ? (
                  <span className="pdChip">Skipped · {q.reason}</span>
                ) : NO_NUMBER.includes(q.status) && finding ? (
                  <span className="pdChip">{q.status === "finding" ? "Finding number…" : "Find while calling"}</span>
                ) : (
                  <span className="pdMuted">{contactClock(q.timeZone)?.label || "local time unknown"}</span>
                )}
              </div>
            ))}
        </div>
      </div>
    </>
  );
});

const PowerDialerDock = ({ taskIds = [] }) => {
  const history = useHistory();
  const { state, loading } = powerDialerStore;

  useEffect(() => {
    document.body.classList.add(BODY_CLASS);
    !powerDialerStore.active && powerDialerStore.start(taskIds);
    return () => {
      document.body.classList.remove(BODY_CLASS);
      powerDialerStore.reset();
    };
  }, []);

  // Click-to-call is blocked (and its floater hidden) only while the session is live; the summary frees it.
  useEffect(() => {
    const live = !!state && state !== STATES.DONE;
    document.body.classList.toggle(LIVE_CLASS, live);
    globalCallState.setLockedMessage(live ? CALLS_LOCKED : "");
  }, [state]);

  useEffect(
    () => () => {
      document.body.classList.remove(LIVE_CLASS);
      globalCallState.setLockedMessage("");
    },
    []
  );

  useEffect(() => {
    const onKey = e => {
      if (confirmationPopupState.showPopup) return;
      const t = e.target;
      const typing = t?.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t?.tagName);
      // Enter on a focused button/link already clicks it.
      if (e.key === "Enter" && ["BUTTON", "A"].includes(t?.tagName)) return;
      const s = powerDialerStore;
      const hit = keyAction(e.key, s.state, typing);
      if (!hit || e.metaKey || e.ctrlKey || e.altKey) return;
      e.preventDefault();
      const run = {
        DIAL: s.dial,
        OUTCOME: () => s.dispositions[hit.index] && s.setWrapupDisposition(s.dispositions[hit.index].id),
        LOG: s.state === STATES.WRAPUP ? s.logWrapup : s.logAutoNow,
        CHANGE: s.changeAutoLog,
        DROP: s.dropVoicemail,
        SKIP: s.skipVoicemail,
        WAIT: s.wait
      }[hit.event];
      run && run();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Leave guard: in-page task switches stay free; any other navigation asks first (B5 / B5b).
  useEffect(() => {
    powerDialerStore.onNoCallingNumber = () => promptNoCallingNumber(history);
    const unblock = history.block(loc => {
      if (inPowerMode(loc) || powerDialerStore.leaveGuard === "none") return;
      askLeave(() => {
        unblock();
        history.push(loc);
      });
      return false;
    });
    const onUnload = e => {
      if (powerDialerStore.leaveGuard === "none") return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onUnload);
    return () => {
      powerDialerStore.onNoCallingNumber = null;
      unblock();
      window.removeEventListener("beforeunload", onUnload);
    };
  }, []);

  // B0: contacts without a number, only when the setting is "Ask me".
  useEffect(() => {
    if (state !== STATES.START || powerDialerStore.noPhoneMode) return;
    const p = noPhonePrompt(powerDialerStore.settings, powerDialerStore.queue);
    if (!p) return;
    const enrich = () => {
      confirmationPopupState.setShowPopup(false);
      powerDialerStore.end("cancelled");
      history.push("/contacts");
    };
    confirmationPopupState.setPopupValues({
      content: (
        <div className="pdPopup">
          <div className="pdPopupTitle">
            {p.noNumber} of {p.total} contacts {p.noNumber === 1 ? "has" : "have"} no phone number
          </div>
          <div className="pdPopupText">
            {p.ready} are ready to dial. For the other {p.noNumber}, we can find their numbers while you call, one at a
            time and just before their turn. Anyone we can’t find is skipped and their task stays due.
          </div>
          <button type="button" className="pdLink" onClick={enrich}>
            Enrich from Contacts first
          </button>
        </div>
      ),
      dynamicDom: true,
      actionBtnText: "Find while calling",
      cancelBtnText: "Skip them",
      callback: () => powerDialerStore.setNoPhoneMode("find"),
      cancelCbk: () => {
        confirmationPopupState.setShowPopup(false);
        powerDialerStore.setNoPhoneMode("skip");
      }
    });
    confirmationPopupState.setShowPopup(true);
  }, [state]);

  const cancel = () => {
    powerDialerStore.end("cancelled");
    history.push("/tasks");
  };

  return ReactDOM.createPortal(
    <aside className="powerDialerDock" aria-label="Power dialer session">
      <div className="pdHead">
        <i className="material-symbols-outlined">phone_forwarded</i>
        <span className="pdHeadTitle">Power dialer</span>
      </div>
      {loading && (
        <div className="pdBody">
          <div className="pdLoader linear-background" />
          <div className="pdLoader linear-background" />
          <div className="pdLoader linear-background" />
        </div>
      )}
      {!loading && state === STATES.START && <StartView />}
      {!loading && state && ![STATES.START, STATES.DONE].includes(state) && <RunningView />}
      {!loading && state === STATES.DONE && <DoneView />}
      <div className="pdFoot">
        {(loading || !state || state === STATES.START) && (
          <button type="button" className="pdGhostBtn" onClick={cancel}>
            Cancel
          </button>
        )}
        {!loading && state && ![STATES.START, STATES.DONE].includes(state) && <RunningFoot />}
        {!loading && state === STATES.DONE && <DoneFoot />}
      </div>
    </aside>,
    document.body
  );
};

export default observer(PowerDialerDock);
